import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { GetContribuinteDto } from "../../../../shared/dtos/Contribuinte.dto";
import contribuinteService from "../../../../services/ContribuinteService";
import { Label } from "../../../../components/Label/styles";
import SelectComponent from "../../../../components/Select/select";
import InputComponent from "../../../../components/Input/input";
import Button from "../../../../components/Button/button";
import {
  BlueTitle,
  ButtonGroupStart,
  Column,
  ContainerWithBorder,
  LoadingGif,
  LoadingOverlay,
  RequiredFields,
  Row,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../../shared/styles/global.styles";
import { isValidCPF } from "../../../../components/ValidateCpf/ValidateCpf";
import {
  PhoneAlert,
  RegistrationNumber,
  RegistrationText,
  CancelButton,
  MainContainer,
} from "./dadosPessoais.styles";
import { IoCloseSharp } from "react-icons/io5";
import loadSCGif from "../../../../assets/load_SC.gif";

interface Props {
  contribuinteEdited?: GetContribuinteDto | any;
  setContribuinteEdited: (contribuinte: GetContribuinteDto) => void;
  onTabChange: (tabIndex: number) => void;
  isNewContribuinte: boolean;
  setIsNewContribuinte: (value: boolean) => void;
  setContribuinteTab: React.Dispatch<React.SetStateAction<GetContribuinteDto>>;
  editOnly: boolean;
}

const DadosPessoais: React.FC<Props> = ({
  contribuinteEdited,
  setContribuinteEdited,
  onTabChange,
  isNewContribuinte,
  setIsNewContribuinte,
  setContribuinteTab,
  editOnly,
}) => {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isRequest, setIsRequest] = useState<boolean>(false);
  const [isCepValid, setIsCepValid] = useState<boolean>(false);
  const [originalValues, setOriginalValues] = useState<any>(null);
  const [isPhoneAlertVisible, setIsPhoneAlertVisible] =
    useState<boolean>(isNewContribuinte);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [contribuinte, setContribuinte] = useState({
    id: undefined,
    cpfCin: "",
    nome: "",
    sexo: 0,
    rg: "",
    dataNascimento: "",
    cep: "",
    endereco: "",
    numeroResidencial: "",
    complementoEndereco: "",
    bairro: "",
    cidade: "",
    uf: "",
    email: "",
    telefoneResidencial: "",
    telefoneComercial: "",
    telefoneRecado: "",
    celular: "",
    cepRemessa: "",
    cidadeRemessa: "",
    ufRemessa: "",
  });

  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | HTMLSelectElement | null;
  }>({
    nome: null,
    cpfCin: null,
    dataNascimento: null,
    celular: null,
    cep: null,
    endereco: null,
    numeroResidencial: null,
    bairro: null,
    cidade: null,
    uf: null,
  });

  const closeAlert = () => {
    setIsPhoneAlertVisible(false);
  };
  const formatCEP = (value: string) => {
    value = value.replace(/\D/g, ""); // Remove caracteres não numéricos
    if (value.length > 0) {
      value = value.replace(/^(\d{5})(\d{3})$/, "$1-$2"); // Formata como xxxxx-xxx
    }
    return value;
  };

  const formatCPF = (value: string) => {
    value = value.replace(/\D/g, "");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    return value;
  };

  const formatRG = (value: string) => {
    value = value.replace(/\D/g, "");
    value = value.replace(/(\d{2})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    return value;
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove caracteres não numéricos
    value = value.replace(/\D/g, "");

    if (value.length === 10) {
      // Formato para telefones fixos (10 dígitos)
      return value.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    } else if (value.length === 11) {
      // Formato para celulares (11 dígitos)
      return value.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }

    // Retorna o valor original formatado parcialmente, se incompleto
    return value;
  };

  const handleInputChangeCpf = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const formattedValue = name === "cpfCin" ? formatCPF(value) : value;
    setContribuinte((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleInputChangeRG = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const formattedValue = name === "rg" ? formatRG(value) : value;
    setContribuinte((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    const formattedValue = (() => {
      switch (name) {
        case "cpfCin":
          return formatCPF(value);
        case "rg":
          return formatRG(value);
        case "cep":
          return formatCEP(value);
        case "celular":
        case "telefoneResidencial":
        case "telefoneComercial":
        case "telefoneRecado":
          return formatPhoneNumber(value);
        case "nome":
          return value.toUpperCase();
        default:
          return value;
      }
    })();

    setContribuinte((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const clearError = async (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    // Remove todos os caracteres não numéricos para trabalhar apenas com números
    const numericValue = value.replace(/\D/g, "");

    switch (name) {
      case "cpfCin":
        // Valida o CPF e adiciona ao invalidFields se for inválido
        if (value.length >= 14 && !isRequest) {
          if (isValidCPF(value)) {
            setIsRequest(true);
            Swal.fire({
              title: "Aguarde",
              text: "Validando CPF...",
              icon: "info",
              allowOutsideClick: false,
              showConfirmButton: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });
            await contribuinteService.searchCpf(value).then((response) => {
              Swal.close();
              if (response.status) {
                Swal.fire({
                  title: "CPF Existente",
                  text: response.message,
                  icon: "error",
                  confirmButtonText: "Ok",
                  allowOutsideClick: false,
                });
                setInvalidFields((prev) => {
                  if (!prev.includes("cpfCin")) return [...prev, "cpfCin"];
                  return prev;
                });
              } else {
                setInvalidFields((prev) =>
                  prev.filter((field) => field !== "cpfCin")
                );
              }
              setIsRequest(false);
            });
          } else {
            Swal.fire({
              title: "CPF Inválido",
              text: "O CPF informado é inválido",
              icon: "error",
              confirmButtonText: "Ok",
              allowOutsideClick: false,
            });
            setInvalidFields((prev) => {
              if (!prev.includes("cpfCin")) return [...prev, "cpfCin"];
              return prev;
            });
          }
        }
        break;
      case "dataNascimento":
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        // Ajusta a idade se o aniversário ainda não ocorreu este ano
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff - 1 < 0)) {
          age--;
        }

        if (age >= 18) {
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "dataNascimento")
          );
        } else {
          Swal.fire({
            title: "Data de Nascimento Inválida",
            text: "O contribuinte titular deve ter a idade mínima de 18 anos",
            icon: "error",
            confirmButtonText: "Ok",
            allowOutsideClick: false,
          });
          setInvalidFields((prev) => [...prev, "dataNascimento"]);
        }
        break;
      case "celular":
      case "telefoneResidencial":
      case "telefoneComercial":
      case "telefoneRecado":
        // Limpa o erro somente quando atingir 10 ou mais dígitos
        if (numericValue.length >= 10) {
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "celular")
          );
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "telefoneResidencial")
          );
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "telefoneComercial")
          );
          setInvalidFields((prev) =>
            prev.filter((field) => field !== "telefoneRecado")
          );
        }
        break;
      case "cep":
        if (numericValue.length === 8) {
          const { isValid, endereco, bairro } = await fetchAddressByCep(value);

          if (isValid) {
            setInvalidFields((prev) => {
              let updatedFields = prev.filter(
                (field) => !["cep", "cidade", "uf"].includes(field)
              );

              if (endereco) {
                updatedFields = updatedFields.filter(
                  (field) => field !== "endereco"
                );
              }

              if (bairro) {
                updatedFields = updatedFields.filter(
                  (field) => field !== "bairro"
                );
              }

              return updatedFields;
            });
          } else {
            setInvalidFields((prev) => {
              if (!prev.includes("cep")) return [...prev, "cep"];
              return prev;
            });
          }
        }
        break;
      default:
        setInvalidFields((prev) => prev.filter((field) => field !== name));
        break;
    }
  };

  // Nova função para buscar endereço pelo ViaCEP
  const fetchAddressByCep = async (
    cep: string
  ): Promise<{ isValid: boolean; endereco: boolean; bairro: boolean }> => {
    try {
      const sanitizedCep = cep.replace(/\D/g, "");

      if (sanitizedCep.length === 8) {
        // Exibe a mensagem de carregamento
        Swal.fire({
          title: "Aguarde",
          text: "Buscando endereço...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await fetch(
          `https://viacep.com.br/ws/${sanitizedCep}/json/`
        );
        const data = await response.json();

        // Fecha o Swal de carregamento
        Swal.close();

        if (data.erro) {
          throw new Error("CEP não encontrado");
        }

        // Atualiza os campos de endereço e remessa com os dados retornados
        setContribuinte((prev) => ({
          ...prev,
          endereco: data.logradouro || "",
          bairro: data.bairro || "",
          cidade: data.localidade || "",
          uf: data.uf || "",
          cepRemessa: sanitizedCep,
          cidadeRemessa: data.localidade || "",
          ufRemessa: data.uf || "",
        }));
        setIsCepValid(true);

        return {
          isValid: true,
          endereco: Boolean(data.logradouro),
          bairro: Boolean(data.bairro),
        };
      }
    } catch (error) {
      // Fecha o Swal de carregamento em caso de erro
      Swal.close();

      // Exibe mensagem de erro e limpa os campos de endereço
      Swal.fire("Erro", "CEP inválido ou não encontrado.", "error");

      setContribuinte((prev) => ({
        ...prev,
        endereco: "",
        bairro: "",
        cidade: "",
        uf: "",
      }));
    }

    return { isValid: false, endereco: false, bairro: false };
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Lista de campos obrigatórios
      const camposObrigatorios: {
        nome: keyof typeof contribuinte;
        label: string;
      }[] = [
        { nome: "nome", label: "Nome" },
        { nome: "cpfCin", label: "CPF" },
        { nome: "sexo", label: "Sexo" },
        { nome: "dataNascimento", label: "Data de Nascimento" },
        { nome: "celular", label: "Celular" },
        { nome: "telefoneComercial", label: "Telefone Comercial" },
        { nome: "telefoneResidencial", label: "Telefone Residencial" },
        { nome: "telefoneRecado", label: "Telefone Recado" },
        { nome: "cep", label: "CEP" },
        { nome: "endereco", label: "Endereço" },
        { nome: "numeroResidencial", label: "Número Residencial" },
        { nome: "bairro", label: "Bairro" },
        { nome: "cidade", label: "Cidade" },
        { nome: "uf", label: "UF" },
      ];

      const telefones = [
        "celular",
        "telefoneComercial",
        "telefoneResidencial",
        "telefoneRecado",
      ] as const;

      const camposNaoPreenchidos = camposObrigatorios.filter((campo) => {
        if (telefones.includes(campo.nome as (typeof telefones)[number])) {
          // Verifica se pelo menos um telefone está preenchido
          return !telefones.some((telefone) => contribuinte[telefone]);
        }

        // Para outros campos, verifica normalmente
        return !contribuinte[campo.nome as keyof typeof contribuinte];
      });

      if (camposNaoPreenchidos.length > 0) {
        const invalidFieldNames = camposNaoPreenchidos.map(
          (campo) => campo.nome
        );
        setInvalidFields(invalidFieldNames);

        Swal.fire({
          title: "Campos Obrigatórios",
          text: "Por favor, preencha todos os campos obrigatórios.",
          icon: "warning",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        }).then(() => {
          // Foca no primeiro campo inválido
          const firstInvalidField = invalidFieldNames[0];
          if (inputRefs.current[firstInvalidField]) {
            inputRefs.current[firstInvalidField]?.focus();
          }
        });

        setLoading(false);
        return;
      }

      setIsSubmitting(true);
      Swal.fire({
        icon: "info",
        title: "Aguarde...",
        text: "Salvando os dados do contribuinte.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const contribuinteData = {
        ...contribuinte,
        dataNascimento: new Date(contribuinte.dataNascimento),
        cidade: contribuinte.cidade || "",
        uf: contribuinte.uf || "",
        cepRemessa: contribuinte.cepRemessa || contribuinte.cep,
        cidadeRemessa: contribuinte.cidadeRemessa || contribuinte.cidade,
        ufRemessa: contribuinte.ufRemessa || contribuinte.uf,
      };

      if (contribuinte.id) {
        await contribuinteService
          .updateContribuinte(contribuinte.id, contribuinteData)
          .then((response) => {
            setContribuinteTab(response.contribuinte);
            const savedData = { ...response.contribuinte };
            if (savedData.dataNascimento) {
              savedData.dataNascimento = new Date(savedData.dataNascimento)
                .toISOString()
                .split("T")[0];
            }
            setOriginalValues(savedData);
            // Desabilita modo de edição após salvar com sucesso
            setIsEditMode(false);
            Swal.fire(
              "Sucesso!",
              "Contribuinte atualizado com sucesso!",
              "success"
            );
          });
      } else {
        let createResponseContribuinte: GetContribuinteDto;
        await contribuinteService
          .createContribuinte(contribuinteData)
          .then((response) => {
            createResponseContribuinte = response.data;
            const savedData = { ...response.data };
            if (savedData.dataNascimento) {
              savedData.dataNascimento = new Date(savedData.dataNascimento)
                .toISOString()
                .split("T")[0];
            }
            setOriginalValues(savedData);
            // Desabilita modo de edição após criar com sucesso
            setIsEditMode(false);
            Swal.fire({
              title: "Sucesso!",
              text: "Contribuinte cadastrado com sucesso! Você será redirecionado para a página de movimentação.",
              icon: "success",
              confirmButtonText: "OK",
              allowOutsideClick: false,
            }).then(() => {
              setContribuinteEdited(createResponseContribuinte);
              onTabChange(3);
              setIsNewContribuinte(true);
            });
          });
      }
    } catch (error) {
      console.error("Erro ao salvar contribuinte:", error);
      Swal.fire(
        "Erro",
        "Erro ao salvar contribuinte. Por favor, tente novamente.",
        "error"
      );
      // Mantém modo de edição em caso de erro
      setIsEditMode(true);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      handleSubmit();
    } else {
      setIsEditMode(true);
      setOriginalValues({ ...contribuinte });
    }
  };

  const cancelEdit = () => {
    if (originalValues) {
      setContribuinte(originalValues);
      setInvalidFields([]);
    }
    setIsEditMode(false);
  };

  // Efeito para inicializar o formulário
  useEffect(() => {
    if (contribuinteEdited) {
      // Se for edição, começa com campos desabilitados
      setIsEditMode(false);

      const formattedContribuinte = {
        ...contribuinteEdited,
        cpfCin: formatCPF(contribuinteEdited.cpfCin || ""),
        cep: formatCEP(contribuinteEdited.cep || ""),
        telefoneResidencial: formatPhoneNumber(
          contribuinteEdited.telefoneResidencial || ""
        ),
        telefoneComercial: formatPhoneNumber(
          contribuinteEdited.telefoneComercial || ""
        ),
        telefoneRecado: formatPhoneNumber(
          contribuinteEdited.telefoneRecado || ""
        ),
        celular: formatPhoneNumber(contribuinteEdited.celular || ""),
        dataNascimento: contribuinteEdited.dataNascimento
          ? new Date(contribuinteEdited.dataNascimento)
              .toISOString()
              .split("T")[0]
          : "",
      };

      setContribuinte(formattedContribuinte);
      setOriginalValues({ ...formattedContribuinte });
    } else {
      // Se for novo cadastro, começa com campos habilitados
      setIsEditMode(true);

      const defaultValues = {
        id: undefined,
        cpfCin: "",
        nome: "",
        sexo: 0,
        rg: "",
        dataNascimento: "",
        cep: "",
        endereco: "",
        numeroResidencial: "",
        complementoEndereco: "",
        bairro: "",
        cidade: "",
        uf: "",
        email: "",
        telefoneResidencial: "",
        telefoneComercial: "",
        telefoneRecado: "",
        celular: "",
        cepRemessa: "",
        cidadeRemessa: "",
        ufRemessa: "",
      };

      setContribuinte(defaultValues);
      setOriginalValues({ ...defaultValues });
    }

    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [contribuinteEdited]);

  // Efeito para controlar o modo de edição baseado em isNewContribuinte
  useEffect(() => {
    setIsEditMode(isNewContribuinte);
  }, [isNewContribuinte]);

  return (
    <MainContainer>
      <ContainerWithBorder>
        {loading ? (
          <LoadingOverlay>
            <LoadingGif src={loadSCGif} alt="Carregando..." />
          </LoadingOverlay>
        ) : (
          <>
            <TitleAndButton>
              <BlueTitle>Dados Pessoais</BlueTitle>
              <RegistrationText>
                Nº Registro:{" "}
                <RegistrationNumber>
                  {contribuinteEdited && contribuinteEdited.id
                    ? contribuinteEdited.id
                    : "0"}
                </RegistrationNumber>
              </RegistrationText>
            </TitleAndButton>

            <StyledHR />
            <RequiredFields>
              (*) Indicativos de campos obrigatórios
            </RequiredFields>
            <Row>
              <Column>
                <Label htmlFor="nome">
                  Nome titular <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="nome"
                  name="nome"
                  value={contribuinte.nome.toUpperCase()}
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (/^[a-zA-ZÀ-ÿ\s]*$/.test(valor)) {
                      handleInputChange(e);
                      clearError(e);
                    }
                  }}
                  placeholder="nome completo"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={155}
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("nome")
                      ? "Preencha o campo Nome titular"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["nome"] = el)}
                />
              </Column>
              <Column basis="32%">
                <Label htmlFor="cpfCin">
                  CPF/CIN <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="cpfCin"
                  name="cpfCin"
                  value={contribuinte.cpfCin}
                  onChange={(e) => {
                    handleInputChangeCpf(e);
                    clearError(e);
                  }}
                  placeholder="000.000.000-00"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={14}
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("cpfCin")
                      ? contribuinte.cpfCin.trim() === ""
                        ? "Preencha o campo CPF/CIN"
                        : "Verifique se o preenchimento do campo CPF/CIN está correto"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["cpfCin"] = el)}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Label htmlFor="rg">RG</Label>
                <InputComponent
                  type="text"
                  id="rg"
                  name="rg"
                  value={contribuinte.rg}
                  onChange={(e) => handleInputChangeRG(e)}
                  placeholder="00.000.000-0"
                  maxLength={12}
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                />
              </Column>
              <Column>
                <Label htmlFor="dataNascimento">
                  Data de nascimento <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="date"
                  id="dataNascimento"
                  name="dataNascimento"
                  value={contribuinte.dataNascimento}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="00/00/0000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("dataNascimento")
                      ? "Preencha o campo Data de Nascimento"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["dataNascimento"] = el)}
                />
              </Column>
              <Column>
                <Label htmlFor="sexo">
                  Sexo <TextError>*</TextError>
                </Label>
                <SelectComponent
                  color="black"
                  placeholder="Selecione"
                  id="sexo"
                  width={Number("100%")}
                  name="sexo"
                  value={contribuinte.sexo.toString()}
                  onChange={(e) => {
                    setContribuinte((prev) => ({
                      ...prev,
                      sexo: parseInt(e.target.value, 10),
                    }));
                    clearError(e);
                  }}
                  options={[
                    { value: "1", label: "Masculino" },
                    { value: "2", label: "Feminino" },
                    { value: "3", label: "Outros" },
                  ]}
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("sexo")
                      ? "A seleção do sexo é obrigatória"
                      : ""
                  }
                />
              </Column>
              <Column>
                <Label htmlFor="email">E-mail</Label>
                <InputComponent
                  type="email"
                  id="email"
                  name="email"
                  value={
                    contribuinte.email
                      ? contribuinte.email.toLowerCase()
                      : contribuinte.email
                  }
                  onChange={handleInputChange}
                  placeholder="email@email.com"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                />
              </Column>
            </Row>
            <BlueTitle>Telefone</BlueTitle>
            <StyledHR />
            {isPhoneAlertVisible && (
              <PhoneAlert>
                <Label style={{ marginBottom: 0 }}>
                  É obrigatório o preenchimento de pelo menos 1 telefone
                </Label>
                <Button
                  type="button"
                  schemaButton="x-close"
                  action={closeAlert}
                  icon={<IoCloseSharp size={30} />}
                />
              </PhoneAlert>
            )}
            <Row>
              <Column>
                <Label htmlFor="celular">Celular</Label>
                <InputComponent
                  type="text"
                  id="celular"
                  name="celular"
                  value={contribuinte.celular}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="(00) 00000-0000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={15}
                  disabled={!isEditMode}
                  errorMessage={invalidFields.includes("celular")}
                  ref={(el) => (inputRefs.current["celular"] = el)}
                />
              </Column>
              <Column>
                <Label htmlFor="telefoneResidencial">
                  Telefone residencial
                </Label>
                <InputComponent
                  type="text"
                  id="telefoneResidencial"
                  name="telefoneResidencial"
                  value={contribuinte.telefoneResidencial}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="(00) 0000-0000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={14}
                  disabled={!isEditMode}
                />
              </Column>
              <Column>
                <Label htmlFor="telefoneComercial">Telefone comercial</Label>
                <InputComponent
                  type="text"
                  id="telefoneComercial"
                  name="telefoneComercial"
                  value={contribuinte.telefoneComercial}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="(00) 00000-0000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={15}
                  disabled={!isEditMode}
                />
              </Column>
              <Column>
                <Label htmlFor="telefoneRecado">Telefone recado</Label>
                <InputComponent
                  type="text"
                  id="telefoneRecado"
                  name="telefoneRecado"
                  value={contribuinte.telefoneRecado}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="(00) 00000-0000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={15}
                  disabled={!isEditMode}
                />
              </Column>
            </Row>
            <BlueTitle>Endereço</BlueTitle>
            <StyledHR />
            <Row>
              <Column basis="40%">
                <Label htmlFor="cep">
                  Cep <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="cep"
                  name="cep"
                  value={contribuinte.cep}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="000000-000"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  maxLength={9}
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("cep")
                      ? contribuinte.cep.trim() === ""
                        ? "Preencha o campo CEP"
                        : "Verifique se o preenchimento do campo CEP está correto"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["cep"] = el)}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Label htmlFor="endereco">
                  Endereço <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="endereco"
                  name="endereco"
                  value={(contribuinte.endereco || "").toUpperCase()}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="endereço completo"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("endereco")
                      ? "Preencha o campo endereço"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["endereco"] = el)}
                />
              </Column>
              <Column basis="40%">
                <Label htmlFor="numeroResidencial">
                  Número <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="number"
                  id="numeroResidencial"
                  name="numeroResidencial"
                  value={contribuinte.numeroResidencial}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="número"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("numeroResidencial")
                      ? "Preencha o campo número"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["numeroResidencial"] = el)}
                />
              </Column>
              <Column basis="70%">
                <Label htmlFor="bairro">
                  Bairro <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="bairro"
                  name="bairro"
                  value={(contribuinte.bairro || "").toUpperCase()}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="nome do bairro"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                  errorMessage={
                    invalidFields.includes("bairro")
                      ? "Preencha o campo bairro"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["bairro"] = el)}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Label htmlFor="cidade">
                  Cidade <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="cidade"
                  name="cidade"
                  value={(contribuinte.cidade || "").toUpperCase()}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="cidade completa"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode || isCepValid}
                  errorMessage={
                    invalidFields.includes("cidade")
                      ? "Preencha o campo cidade"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["cidade"] = el)}
                />
              </Column>
              <Column>
                <Label htmlFor="uf">
                  Estado <TextError>*</TextError>
                </Label>
                <InputComponent
                  type="text"
                  id="uf"
                  name="uf"
                  value={contribuinte.uf}
                  onChange={(e) => {
                    handleInputChange(e);
                    clearError(e);
                  }}
                  placeholder="sigla do estado"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode || isCepValid}
                  errorMessage={
                    invalidFields.includes("uf")
                      ? "Preencha o campo estado"
                      : ""
                  }
                  ref={(el) => (inputRefs.current["uf"] = el)}
                />
              </Column>
              <Column>
                <Label htmlFor="complementoEndereco">Complemento</Label>
                <InputComponent
                  type="text"
                  id="complementoEndereco"
                  name="complementoEndereco"
                  value={(contribuinte.complementoEndereco || "").toUpperCase()}
                  onChange={handleInputChange}
                  placeholder="ex: bloco, apartamento, fundos"
                  color="#333"
                  width={Number("100%")}
                  border="1px solid #ccc"
                  disabled={!isEditMode}
                />
              </Column>
            </Row>
            <StyledHR />
            {editOnly && (
              <ButtonGroupStart>
                {contribuinteEdited ? (
                  // Botões para edição de contribuinte existente
                  <>
                    <Button
                      schemaButton="full-primary"
                      action={toggleEditMode}
                      type="button"
                      disabled={
                        isSubmitting || (isEditMode && invalidFields.length > 0)
                      }
                    >
                      {isEditMode ? "Salvar" : "Editar"}
                    </Button>

                    {isEditMode && (
                      <CancelButton>
                        <Button
                          schemaButton="out-secondary"
                          action={cancelEdit}
                          type="button"
                          disabled={isSubmitting}
                        >
                          Cancelar
                        </Button>
                      </CancelButton>
                    )}
                  </>
                ) : (
                  // Botões para novo cadastro - apenas botão de salvar
                  <Button
                    schemaButton="full-primary"
                    action={handleSubmit}
                    type="button"
                    disabled={isSubmitting || invalidFields.length > 0}
                  >
                    Salvar
                  </Button>
                )}
              </ButtonGroupStart>
            )}
          </>
        )}
      </ContainerWithBorder>
    </MainContainer>
  );
};

export default DadosPessoais;
